import { getOrCreateDeviceId } from "./identity";

const VIEW_SENT_KEY = "so_buy_again_widget_view_sent";

function hasViewBeenSentThisSession(): boolean {
  return (
    typeof window !== "undefined" &&
    sessionStorage.getItem(VIEW_SENT_KEY) === "1"
  );
}

function markViewSentThisSession(): void {
  if (typeof window !== "undefined") sessionStorage.setItem(VIEW_SENT_KEY, "1");
}

type AnalyticsPayload = {
  event_type: string;
  visitorId?: string;
  productId?: number;
  url?: string;
  properties?: Record<string, unknown>;
};

type InteractAction =
  | "open_panel"
  | "add_to_cart"
  | "buy_now"
  | "subscribe_now"
  | "checkout"
  | "close"
  | "customize"
  | "subscription_pause"
  | "subscription_extend_pause"
  | "subscription_unpause"
  | "subscription_get_it_now"
  | "subscription_skip_next_order"
  | "subscription_delay"
  | "subscription_change_frequency"
  | "subscription_contact_support"
  | "subscription_merge_banner_shown"
  | "subscription_merge_start"
  | "subscription_merge_submit"
  | "subscription_merge_failed"
  | "subscription_merge_email_landed"
  | "subscription_merge_dismiss"
  | "subscription_size_up_banner_shown"
  | "subscription_size_up_start"
  | "subscription_size_up_submit"
  | "subscription_size_up_failed"
  | "subscription_size_up_email_landed"
  | "subscription_size_up_dismiss"
  | "subscription_reactivate"
  | "subscription_manage_open"
  | "subscription_cancel_open"
  | "subscription_cancel_why_bought_continue"
  // The opening "what drew you to X?" question in the cancel chat: the chips being
  // shown, and the answer itself — captured verbatim as the customer sends it, so
  // it survives an abandoned chat and a tap stays distinguishable from typed text.
  // See manage-subscription/cancel-chat-intake.ts.
  | "subscription_cancel_motivation_shown"
  | "subscription_cancel_motivation_answered"
  | "subscription_cancel_reason_selected"
  // On a 2+ line contract, which line they chose to cancel (or "everything") in
  // the app-rendered which-product question — the fork between a full and a
  // partial cancel, and the only signal for WHICH line partial-intent customers
  // drop. See manage-subscription/cancel-chat-scope.ts.
  | "subscription_cancel_product_choice"
  // The ROOT behind the cancel reason — the thing that picks the save. "Too
  // expensive" can mean a tight budget (→ pay less often) or a value judgement
  // (→ a different product); opposite problems, opposite answers. Tracked both as
  // a property on the intake event and on its own, so the root is queryable
  // directly. See shared/cancel-sub-reason.
  | "subscription_cancel_sub_reason_selected"
  | "subscription_cancel_continue"
  | "subscription_cancel_confirmed"
  | "subscription_cancel_scheduled"
  | "subscription_cancel_schedule_undone"
  | "subscription_cancel_today_from_scheduled"
  | "subscription_retention_offer_shown"
  | "subscription_retention_offer_accepted"
  | "subscription_retention_offer_declined"
  | "subscription_swap_accepted"
  // The computed size-up bundle (price branch) — shown to the customer, and taken.
  | "subscription_bundle_shown"
  | "subscription_bundle_accepted"
  // The points-gift offer under the rating thank-you: shown, and "Get it" tapped.
  | "subscription_gift_offer_shown"
  | "subscription_gift_offer_clicked"
  | "widget_login_otp_sent"
  | "widget_login_success"
  // Sign-in refused at the email step: no customer record behind the address.
  | "widget_login_email_unknown"
  | "widget_logout"
  | "welcome_offer_click";

export function trackBuyAgainWidgetView(
  productCount: number,
  _shop: string,
): void {
  if (typeof window === "undefined") return;
  if (hasViewBeenSentThisSession()) return;
  markViewSentThisSession();
  const payload: AnalyticsPayload = {
    event_type: "widget.viewed",
    visitorId: getOrCreateDeviceId(),
    url: window.location.href,
    properties: { product_count: productCount },
  };
  window.Shopify?.analytics?.publish?.("widget.viewed", payload);
}

export type CancelFlowVariant = "chat" | "dialog";

/**
 * What events are stamped with: the assigned arm, "unassigned" while the
 * rollout config has not resolved yet (excluded from both arms in analysis —
 * never default to a real arm), or "forced" for internal-testing overrides
 * (query param / dev flag) so tester traffic can't pollute the A/B.
 */
export type CancelFlowStamp = CancelFlowVariant | "unassigned" | "forced";

// The customer's cancellation-flow arm (AI chat vs. legacy dialog), set by
// ManageSubscriptionSheet from the runtime rollout config. While set, every
// interact event carries it, so the A/B splits cleanly in PostHog across the
// sheet, chat, dialog, and offer-body events without threading a prop through
// each component. Intent-to-treat: this is the ASSIGNED arm — a chat
// fatal-error fallback to the dialog does not change it. Deliberately never
// cleared on unmount: later session events (reactivation etc.) stay
// attributable to the arm.
let cancelFlowVariant: CancelFlowStamp | null = null;

export function setCancelFlowVariant(variant: CancelFlowStamp | null): void {
  cancelFlowVariant = variant;
}

// The combine-email notification id (`?so_combine_nid`), set once on arrival
// from the Klaviyo CTA. While set, every interact event carries it, so
// email-driven combines separate cleanly from organic ones in PostHog without
// threading a prop through the panel, banner and dialog. Deliberately never
// cleared: a customer who wanders off and combines later in the same session
// is still email-attributed.
let combineNotificationId: string | null = null;

export function setCombineNotificationId(id: string | null): void {
  combineNotificationId = id;
}

// Size-up email notification id (`?so_sizeup_nid`) — same stamping pattern as
// combine, so email-driven size-ups separate from organic ones in PostHog.
let sizeUpNotificationId: string | null = null;

export function setSizeUpNotificationId(id: string | null): void {
  sizeUpNotificationId = id;
}

export function trackBuyAgainWidgetInteract(
  action: InteractAction,
  _shop: string,
  data?: {
    productId?: number;
    variantId?: number;
    [key: string]: unknown;
  },
): void {
  if (typeof window === "undefined") return;
  const { productId, ...extraProperties } = data ?? {};
  const properties: Record<string, unknown> = { action };
  if (cancelFlowVariant) properties.cancel_flow_variant = cancelFlowVariant;
  if (combineNotificationId) {
    properties.combine_notification_id = combineNotificationId;
  }
  if (sizeUpNotificationId) {
    properties.sizeup_notification_id = sizeUpNotificationId;
  }
  for (const [key, value] of Object.entries(extraProperties)) {
    if (value !== undefined && value !== null) {
      properties[key] = value;
    }
  }
  const payload: AnalyticsPayload = {
    event_type: "widget.interacted",
    visitorId: getOrCreateDeviceId(),
    url: window.location.href,
    productId,
    properties,
  };
  window.Shopify?.analytics?.publish?.("widget.interacted", payload);
}

export function getShopForTracking(
  products: { shopify_shop_domain?: string | null }[],
): string | null {
  const fromProduct = products[0]?.shopify_shop_domain;
  if (fromProduct?.trim()) return fromProduct.trim();
  if (typeof window !== "undefined" && window.Shopify?.shop) {
    return window.Shopify.shop;
  }
  return null;
}

export type SubscriptionRatingActionType =
  | "pause"
  | "unpause"
  | "skip_next_order"
  | "get_it_now"
  | "change_frequency"
  | "change_delivery_date"
  | "extend_pause"
  | "reactivate";

function publishSubscriptionRating(
  event:
    | "subscription_rating.prompt_shown"
    | "subscription_rating.submitted"
    | "subscription_rating.dismissed",
  data: {
    productId: number;
    actionType: SubscriptionRatingActionType;
    rating?: 1 | 2 | 3 | 4 | 5;
  },
): void {
  if (typeof window === "undefined") return;
  const payload = {
    event_type: event,
    visitorId: getOrCreateDeviceId(),
    productId: data.productId,
    url: window.location.href,
    actionType: data.actionType,
    ...(data.rating != null ? { rating: data.rating } : {}),
  };
  window.Shopify?.analytics?.publish?.(event, payload);
}

export function trackSubscriptionRatingPromptShown(data: {
  productId: number;
  actionType: SubscriptionRatingActionType;
}): void {
  publishSubscriptionRating("subscription_rating.prompt_shown", data);
}

export function trackSubscriptionRatingSubmitted(data: {
  productId: number;
  actionType: SubscriptionRatingActionType;
  rating: 1 | 2 | 3 | 4 | 5;
}): void {
  publishSubscriptionRating("subscription_rating.submitted", data);
}

export function trackSubscriptionRatingDismissed(data: {
  productId: number;
  actionType: SubscriptionRatingActionType;
}): void {
  publishSubscriptionRating("subscription_rating.dismissed", data);
}

/**
 * The points-gift offer under the rating thank-you (see ActionResultDialog).
 * Rides widget.interacted like the other banner/offer events.
 */
export function trackSubscriptionGiftOfferShown(data: {
  shop: string;
  productId: number;
  actionType: SubscriptionRatingActionType;
}): void {
  trackBuyAgainWidgetInteract("subscription_gift_offer_shown", data.shop, {
    productId: data.productId,
    action_type: data.actionType,
  });
}

export function trackSubscriptionGiftOfferClicked(data: {
  shop: string;
  productId: number;
  actionType: SubscriptionRatingActionType;
}): void {
  trackBuyAgainWidgetInteract("subscription_gift_offer_clicked", data.shop, {
    productId: data.productId,
    action_type: data.actionType,
  });
}
